/**
 * Cloud Sync Module — localStorage ↔ Supabase Postgres.
 *
 * Owns: the mirror, and the record of what has not yet been mirrored.
 * Does not own: when a write happens, or what any collection means.
 *
 * Strategy:
 *   • localStorage is the write-through cache (instant reads, offline capable).
 *   • On every data write, the owning module calls push(name, data) here,
 *     fire-and-forget.
 *   • A push that the server does not confirm leaves the collection DIRTY.
 *     A pull refuses to overwrite a dirty collection, and flush() retries it on
 *     reconnect and at sign-in.
 *   • On login, loadAllFromCloud() pulls the latest and merges into localStorage.
 *     Cloud wins on conflict — EXCEPT for a dirty collection, where local wins,
 *     because dirty means the cloud copy is older by construction.
 *
 * Exposes window.CloudSync = { COLLECTIONS, push, flush, isDirty,
 *                              loadAllFromCloud, restoreNotes }
 */
// Assigned by the IIFE below, then re-exported. The IIFE is kept intact rather
// than unwrapped so the diff stays reviewable.
let _exported;

(function () {

    const db = () => window.SupabaseClient;

    // ── The registry ──────────────────────────────────────────────────────────
    // One entry per synced collection. This table drives push, pull, delete
    // reconciliation and retry, so adding a collection is one line here plus one
    // call site — and tests/cloud-sync-wiring.test.js fails if either is missing.
    //
    // It exists because nine hand-written push* functions drifted out of step
    // with their call sites: pushTimerSessions and pushFocusStats shipped
    // exported and called by nothing, while the pull read both.
    //
    // NOT here, deliberately: PDFs (IndexedDB, needs supabase.storage), Meneer
    // threads (pushing the whole store per message is unaffordable on a metered
    // connection), device UI preferences, and transient handoffs like
    // focus_session. See the plan in docs/superpowers/plans/.
    const COLLECTIONS = {
        notes:         { kind: 'rows', table: 'notes',           key: 'productivity_notes' },
        decks:         { kind: 'rows', table: 'flashcard_decks', key: 'productivity_decks' },
        quizzes:       { kind: 'rows', table: 'custom_quizzes',  key: 'productivity_custom_quizzes' },
        subjects:      { kind: 'rows', table: 'subjects',        key: 'studymeneer_subjects' },
        tasks:         { kind: 'blob', field: 'tasks',           key: 'productivity_tasks' },
        events:        { kind: 'blob', field: 'events',          key: 'productivity_events' },
        settings:      { kind: 'blob', field: 'settings',        key: 'productivity_settings' },
        stats:         { kind: 'blob', field: 'stats',           key: 'productivity_stats' },
        timerSessions: { kind: 'blob', field: 'timer_sessions',  key: 'productivity_timer_sessions' },
        focusStats:    { kind: 'blob', field: 'focus_stats',     key: 'focus_stats' },
        quizResults:   { kind: 'blob', field: 'quiz_results',    key: 'productivity_quiz_results' },
        mistakeLog:    { kind: 'blob', field: 'mistake_log',     key: 'productivity_mistake_log' },
        studyStreak:   { kind: 'blob', field: 'study_streak',    key: 'productivity_streak' },
        studyTime:     { kind: 'blob', field: 'study_time',      key: 'productivity_study_time' },
        timers:        { kind: 'blob', field: 'timers',          key: 'productivity_timers' },
        customPresets: { kind: 'blob', field: 'custom_presets',  key: 'productivity_custom_presets' }
    };

    // ── The dirty ledger ──────────────────────────────────────────────────────
    // Collections written locally but not confirmed by the server.
    const DIRTY_KEY = 'sync_dirty';

    function _readDirty() {
        try {
            const parsed = JSON.parse(localStorage.getItem(DIRTY_KEY) || '{}');
            return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
        } catch (_) { return {}; }
    }

    function _setDirty(name, value) {
        try {
            const map = _readDirty();
            if (value) map[name] = true; else delete map[name];
            localStorage.setItem(DIRTY_KEY, JSON.stringify(map));
        } catch (_) { /* the ledger is an optimisation; never fail a save for it */ }
    }

    function isDirty(name) { return _readDirty()[name] === true; }

    function isAnyDirty() {
        const map = _readDirty();
        return Object.keys(COLLECTIONS).some(name => map[name] === true);
    }

    // ── Proof of what the cloud contains ──────────────────────────────────────
    // Collections whose cloud copy has been read SUCCESSFULLY, this page load.
    //
    // Deleting a row is the one irreversible thing this module does, and it is
    // only safe when the local array is known to be a superset of the cloud —
    // which is true after a pull and not before. A failed pull returns nothing,
    // is indistinguishable from an empty cloud, and would otherwise let a stale
    // local array delete every row the pull never brought down.
    //
    // Deliberately in memory rather than in localStorage: a persisted "the pull
    // worked" would let last week's success authorise a delete today, which is
    // the same class of mistake as trusting a stale cache.
    const _pulled = new Set();

    /**
     * The signed-in user id, resolved WITHOUT a network call.
     *
     * getUser() round-trips to /auth/v1/user, so offline it rejects — and a write
     * whose user could not be resolved was never recorded as needing a push. That
     * is precisely how work written offline was dropped and then overwritten by
     * the next pull. getSession() reads the persisted session locally.
     */
    async function _userId() {
        if (!db()) return null;
        const { data: { session } } = await db().auth.getSession();
        return session && session.user ? session.user.id : null;
    }

    /** Empty enough that writing it over a local value would be a loss, not a sync. */
    function _isEmpty(value) {
        if (value === null || value === undefined) return true;
        if (Array.isArray(value)) return value.length === 0;
        if (typeof value === 'string') return value.length === 0;
        if (typeof value === 'number') return value === 0;
        if (typeof value === 'object') return Object.keys(value).length === 0;
        return false;
    }

    // ── Push ──────────────────────────────────────────────────────────────────

    /**
     * Mirror one collection to Supabase.
     *
     * Fire-and-forget by contract: never rejects, never throws, and anything the
     * server does not confirm stays dirty so flush() retries it. A sync failure
     * is not a reason for a save to fail (AGENTS.md SKILL 2).
     */
    async function push(name, data) {
        const spec = COLLECTIONS[name];
        if (!spec) { console.warn(`CloudSync: unknown collection "${name}"`); return; }
        try {
            const uid = await _userId();
            if (!uid) return;          // signed out — nothing to sync to, and not dirty
            _setDirty(name, true);     // set BEFORE the write, so a crash fails safe
            const ok = spec.kind === 'rows'
                ? await _pushRows(spec.table, uid, data, _pulled.has(name))
                : await _pushBlob(uid, spec.field, data);
            if (ok) _setDirty(name, false);
        } catch (err) {
            console.warn(`CloudSync: push ${name} failed —`, err && err.message);
        }
    }

    async function _pushRows(table, userId, items, mayReconcile) {
        if (!Array.isArray(items)) return false;

        // An empty array is indistinguishable from "this device has not pulled
        // yet", so it is never allowed to mean "delete everything" — one failed
        // pull would otherwise wipe the student's entire cloud copy. The mirror
        // image of restoreNotes() refusing to treat an empty cloud as a restore.
        // Cost: deleting your last record does not propagate until there is at
        // least one left. That is the safe direction to be wrong in.
        if (items.length === 0) return true;

        const rows = items.map(item => ({
            id:      item && item.id ? item.id : String(Date.now() + Math.random()),
            user_id: userId,
            data:    item,
            updated_at: new Date().toISOString()
        }));

        const { error } = await db().from(table).upsert(rows, { onConflict: 'id' });
        if (error) { console.warn(`CloudSync: upsert ${table} failed —`, error.message); return false; }

        // The upsert succeeded, so the student's data IS in the cloud — that is
        // what "confirmed" means, and the collection is no longer dirty either
        // way. Withholding the delete only postpones a deletion propagating
        // until a session that has actually read the cloud. Safe direction.
        if (!mayReconcile) {
            console.info(`CloudSync: not reconciling ${table} deletes — no confirmed pull this session`);
            return true;
        }

        return _reconcileDeletes(table, userId, rows.map(r => r.id));
    }

    /**
     * Remove rows the student has deleted locally.
     *
     * Without this the mirror never forgets: _fetchRows selects every row for the
     * user, so a deleted note, deck or quiz reappeared on the next login. The
     * local array is authoritative — anything in the table that is not in it is
     * gone.
     */
    async function _reconcileDeletes(table, userId, keepIds) {
        const { data, error } = await db().from(table).select('id').eq('user_id', userId);
        if (error) { console.warn(`CloudSync: delete scan ${table} failed —`, error.message); return false; }

        const keep = new Set(keepIds);
        const stale = (data || []).map(r => r.id).filter(id => id && !keep.has(id));
        if (stale.length === 0) return true;

        const { error: delError } = await db().from(table).delete().eq('user_id', userId).in('id', stale);
        if (delError) { console.warn(`CloudSync: delete ${table} failed —`, delError.message); return false; }
        return true;
    }

    async function _pushBlob(userId, field, value) {
        const { error } = await db().from('user_blobs').upsert(
            { user_id: userId, [field]: value, updated_at: new Date().toISOString() },
            { onConflict: 'user_id' }
        );
        if (error) { console.warn(`CloudSync: upsert user_blobs.${field} failed —`, error.message); return false; }
        return true;
    }

    /** Retry every collection the server has not confirmed. */
    async function flush() {
        const names = Object.keys(_readDirty()).filter(n => COLLECTIONS[n]);
        for (const name of names) {
            let value;
            try {
                const raw = localStorage.getItem(COLLECTIONS[name].key);
                if (raw === null) { _setDirty(name, false); continue; }
                value = JSON.parse(raw);
            } catch (_) {
                // Unreadable local data is storage.js's problem to quarantine, not
                // sync's to propagate. Pushing it would overwrite a good cloud copy
                // with garbage. Leave it dirty and leave the cloud alone.
                console.warn(`CloudSync: skipping flush of unreadable "${name}"`);
                continue;
            }
            await push(name, value);
        }
    }

    // ── Fetch helpers ─────────────────────────────────────────────────────────

    // Both report `ok` separately from their payload. An empty result and a
    // failed read are the same value and must never be the same answer: one
    // means the cloud is empty, the other means we do not know what it holds,
    // and only the first can justify deleting anything.
    async function _fetchRows(table, userId) {
        if (!db()) return { ok: false, rows: [] };
        const { data, error } = await db().from(table).select('data').eq('user_id', userId);
        if (error) { console.warn(`CloudSync: fetch ${table} failed —`, error.message); return { ok: false, rows: [] }; }
        return { ok: true, rows: (data || []).map(r => r.data) };
    }

    async function _fetchBlob(userId) {
        if (!db()) return { ok: false, data: null };
        const { data, error } = await db().from('user_blobs').select('*').eq('user_id', userId).maybeSingle();
        if (error) { console.warn('CloudSync: fetch user_blobs failed —', error.message); return { ok: false, data: null }; }
        return { ok: true, data };
    }

    // ── Pull ──────────────────────────────────────────────────────────────────

    /**
     * Fetch everything from Supabase and merge into localStorage.
     *
     * Called once per session-ready — which, note, is EVERY page load with a
     * valid session, not only a fresh sign-in. Two rules follow from that:
     *
     *   • an EMPTY cloud value never overwrites a local one, so a first sign-in
     *     on a device that already has work does not wipe it; and
     *   • a DIRTY collection is never overwritten at all. Dirty means local holds
     *     writes the server has not confirmed, so the cloud copy is older by
     *     construction. It is pushed up instead.
     */
    async function loadAllFromCloud(userId) {
        try {
            const rowNames = Object.keys(COLLECTIONS).filter(n => COLLECTIONS[n].kind === 'rows');
            const [rowResults, blobResult] = await Promise.all([
                Promise.all(rowNames.map(n => _fetchRows(COLLECTIONS[n].table, userId))),
                _fetchBlob(userId)
            ]);

            // Marking happens per collection and only on a confirmed read, so one
            // table failing does not authorise deletes anywhere else — or block
            // them everywhere else either.
            rowNames.forEach((name, i) => {
                if (!rowResults[i].ok) return;
                _pulled.add(name);
                _applyPulled(name, rowResults[i].rows);
            });

            if (blobResult.ok) {
                Object.keys(COLLECTIONS)
                    .filter(n => COLLECTIONS[n].kind === 'blob')
                    .forEach(name => {
                        _pulled.add(name);
                        _applyPulled(name, blobResult.data ? blobResult.data[COLLECTIONS[name].field] : undefined);
                    });
            }
        } catch (err) {
            console.warn('CloudSync: loadAllFromCloud failed —', err && err.message);
        }

        // Anything still dirty holds local writes the server has not seen. Push
        // now, so the two copies converge in the direction that cannot lose work.
        await flush();
    }

    function _applyPulled(name, value) {
        if (isDirty(name)) {
            console.info(`CloudSync: keeping local "${name}" — it has unpushed changes`);
            return;
        }
        if (_isEmpty(value)) return;
        try {
            localStorage.setItem(COLLECTIONS[name].key, JSON.stringify(value));
        } catch (err) {
            console.warn(`CloudSync: could not store pulled ${name} —`, err && err.message);
        }
    }

    // ── Recovery ──────────────────────────────────────────────────────────────

    /**
     * Restore notes from the cloud after local corruption (roadmap Phase 1).
     *
     * storage.js:loadNotes() has always called window.CloudSync.restoreNotes() when
     * the local copy fails to parse — but nothing ever defined it, so the condition
     * was permanently false and the branch was dead. The hook looked implemented.
     *
     * Deliberately conservative, because this runs when the student's data is
     * already damaged:
     *   - never runs when signed out; there is nothing to restore from
     *   - never overwrites a good local copy, only a quarantined one
     *   - an empty cloud is NOT a restore. Writing [] over a corrupt-but-present
     *     local copy would turn a recoverable problem into data loss
     *   - the quarantined copy in productivity_notes__corrupt is left untouched
     *
     * This one keeps getUser() rather than getSession(): it is an explicit,
     * online-only recovery action, so confirming the user against the server is
     * the right trade here — unlike push(), which must work offline.
     */
    async function restoreNotes() {
        try {
            if (!db()) {
                _notify('Could not reach the cloud to restore your notes.', 'error');
                return false;
            }
            const { data: { user } } = await db().auth.getUser();
            if (!user) {
                _notify('Sign in to restore your notes from the cloud.', 'error');
                return false;
            }

            const { ok, rows: notes } = await _fetchRows('notes', user.id);
            if (!ok || !Array.isArray(notes) || notes.length === 0) {
                // An empty result is indistinguishable from "never synced". Refuse.
                _notify('No cloud backup found. Your unreadable notes have been kept on this device.', 'error');
                return false;
            }

            localStorage.setItem('productivity_notes', JSON.stringify(notes));
            _notify(`Restored ${notes.length} note${notes.length > 1 ? 's' : ''} from the cloud.`, 'success');
            window.EventBus?.emit('notes:changed', { notes });
            return true;
        } catch (err) {
            console.warn('CloudSync: restoreNotes failed —', err.message);
            _notify('Could not restore your notes. The unreadable copy is still on this device.', 'error');
            return false;
        }
    }

    function _notify(message, level) {
        if (typeof window.showNotification === 'function') window.showNotification(message, level);
        else console.warn('[CloudSync]', message);
    }

    // ── Reconnect retry ───────────────────────────────────────────────────────
    // quizzes.js already does this for pending AI quiz requests; sync had no
    // equivalent, so a push that failed stayed failed until the next sign-in —
    // and the next sign-in is exactly when the stale cloud copy overwrote it.
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('online', () => { flush(); });
    }

    // ── Public API ────────────────────────────────────────────────────────────
    const CloudSync = {
        COLLECTIONS,
        push,
        flush,
        isDirty,
        isAnyDirty,
        loadAllFromCloud,
        restoreNotes
    };

    // Transition shim (AGENTS.md SKILL 1). storage.js, subjects.js and focus.js
    // reach this through window.CloudSync at call time.
    if (typeof window !== 'undefined') window.CloudSync = CloudSync;
    _exported = CloudSync;

})();

// ES module surface. Converted per roadmap §3; deferred, so it evaluates after
// every classic script but before DOMContentLoaded — and its consumers only touch
// window.CloudSync from inside functions called later.
export const CloudSync = _exported;
export default _exported;
